
import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, Inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, Subject } from "rxjs";
import { FacebookService } from '@app/@shared/services/api/facebook.service';
import { GoogleOauthService } from '@app/@shared/services/api/google-oauth.service';
import { ToastrService } from '@app/@shared/services/web/toastr.service';
import { AuthenticationService } from '@app/auth/authentication.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { untilDestroyed } from "@core/until-destroyed";
import { SignInModalComponent } from '../sign-in-modal/sign-in-modal.component';
import { ActionsService } from '@app/@shared/services/web/actions.service';
import { DatalayerService } from '@app/@shared/services/web/datalayer.service';
import { CookieService } from 'ngx-cookie-service';
import { UsersService } from '@app/@shared/services/api/users-account.service';
import { CredentialsService } from '@app/@shared/services/web/credentials.service';


declare var google: any;

@Component({
  selector: 'app-login-modal',
  templateUrl: './login-modal.component.html',
  styleUrls: ['./login-modal.component.scss']
})
export class LoginModalComponent implements AfterViewInit, OnDestroy {

  public googleRequest: any;
  public currentLang = '';
  public loginForm!: FormGroup;
  public step = 0;
  public cookieHsUtk = '';
  public loadingLogin = false;
  /** true cuando el email del login no tiene cuenta registrada; muestra aviso con link a Crear cuenta. */
  public emailNotFound = false;
  /** true cuando el login falló por contraseña incorrecta; muestra el link de recuperar contraseña. */
  public wrongPassword = false;
  public showPassword = false;

  private destroy$ = new Subject<void>();
  // Survey
  public surveyObjective: { value: string; code: string } = { value: '', code: '' };
  public surveyRisk: { value: string; code: string } = { value: '', code: '' };
  public surveyInvolvement: { value: string; code: string } = { value: '', code: '' };
  public surveyDecision: { value: string; code: string } = { value: '', code: '' };
  public surveyPhrase: { value: string; code: string } = { value: '', code: '' };

  // Preferences
  public prefZones = '';
  public prefSelectedZone = '';
  public prefMinTicket = '';
  public prefMaxTicket = '';
  public prefAssetTypes: string[] = [];
  public prefPropertiesCount = '';
  public prefProblematic = false;
  public preferencesForm!: FormGroup;

  private stepHistory: number[] = [];

  constructor(
    public dialogRef: MatDialogRef<LoginModalComponent>,
    public translateService: TranslateService,
    public dialog: MatDialog,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
    private authenticationService: AuthenticationService,
    private facebookService: FacebookService,
    private googleOauth: GoogleOauthService,
    private formBuilder: FormBuilder,
    private actionsService: ActionsService,
    private router: Router,
    @Inject(MAT_DIALOG_DATA) public data: any,
    @Inject(PLATFORM_ID) private platformId: any,
    private datalayerService: DatalayerService,
    private cookieService: CookieService,
    private usersService: UsersService,
    private credentialsService: CredentialsService,
  ) {
    this.createForm();
    // FormGroup vacío siempre válido — el usuario ya está registrado
    this.preferencesForm = this.formBuilder.group({});
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.googleRequest = google.accounts.oauth2.initTokenClient({
        client_id: environment.GOOGLE_APP_ID,
        scope:
          'https://www.googleapis.com/auth/userinfo.profile ' +
          'https://www.googleapis.com/auth/userinfo.email ' +
          'https://www.googleapis.com/auth/plus.me',
        prompt: 'consent',
        callback: (tokenResponse: any) => {
          this.googleOauth
            .getUserInfo(tokenResponse.access_token)
            .subscribe((userResponse: any) => {
              this.loadingLogin = true;
              this.loginWithGoogle(tokenResponse.access_token);
            });
        },
      });
    }
  }

  createForm() {
    this.loginForm = this.formBuilder.group({
      email: ['', Validators.required],
      password: ['', Validators.required]
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleAssetType(type: string): void {
    const idx = this.prefAssetTypes.indexOf(type);
    if (idx >= 0) this.prefAssetTypes.splice(idx, 1);
    else this.prefAssetTypes.push(type);
  }

  submitInvestorProfile(): void {
    // Conectar con usersService.saveInvestorProfile() cuando el servicio esté listo
    const idUser = this.credentialsService.credentials?.id_user;
    const uEmail = this.credentialsService.credentials?.email;

    /** Payload test perfil Inversor */
    const formPerfilInversor = {
      cual_es_tu_objetivo_principal_al_invertir_en_un_inmueble: this.surveyObjective.value,
      code_answ1: this.surveyObjective.code,
      que_nivel_de_riesgo_estas_dispuesto_a_asumir: this.surveyRisk.value,
      code_answ2: this.surveyRisk.code,
      como_quieres_implicarte_en_la_inversion: this.surveyInvolvement.value,
      code_answ3: this.surveyInvolvement.code,
      que_te_da_mas_tranquilidad_al_tomar_una_decision: this.surveyDecision.value,
      code_answ4: this.surveyDecision.code,
      que_frase_describe_mejor_como_quieres_que_funcione_esta_inversion_para_ti: this.surveyPhrase.value,
      code_answ5: this.surveyPhrase.code,
      form_id: '6b0c153e-8550-43fe-84b0-88811cb8e9e9',
      form_name: 'Perfil Inversor',
      form_location: 'modal',
      page_language: this.translateService.getCurrentLang(),
      page_location: isPlatformBrowser(this.platformId)
        ? window.location.href
        : '',
      cookieHsUtk: this.cookieService.get('hubspotutk'),
    };

    console.log('idUser:', idUser);
    console.log('formPerfilInversor:', formPerfilInversor);
    console.log('email:', uEmail);

    this.usersService.updateUserProfile(idUser, uEmail, formPerfilInversor).subscribe({
      next: () => {
        this.toastrService.success('Perfil de inversor actualizado', '');
        this.checkRoute();
        this.dialogRef.close();
      },
      error: (error: any) => {
        this.toastrService.error(error.error.message, 'Error al actualizar el perfil de inversor.');
      },
    });

  }

  login(user?: any) {
    this.loadingLogin = true;
    this.emailNotFound = false;
    this.wrongPassword = false;
    if (user) this.loginForm.get('email')?.setValue(user.email);
    const general_data = {
      cookieHsUtk: this.cookieService.get('hubspotutk'),
      page: isPlatformBrowser(this.platformId) ? window.location.href : '',
      name: 'Login for email',
    }
    const payload = {
      ...this.loginForm.value,
      general_data
    };

    const login$ = this.authenticationService.login(payload);
    login$
      .pipe(
        finalize(() => {
          this.loginForm.markAsPristine();
          this.loadingLogin = false;
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: (response) => {
          let dataUser: any = {};
          response.subscribe((realData: any) => {
            dataUser = realData;
          });

          let missingResponseInvestor = false;
          let missingResponseSeller = false;

          const idUserType = dataUser?.userTypes?.id_user_type;
          const profileType = dataUser?.profile_type;
          const userAnswersLength = dataUser?.userAnswers?.length || 0;

          console.log("id_user_type:", idUserType);
          console.log("userAnswers length:", userAnswersLength);
          console.log("profile_type:", profileType);

          /**
           * Reglas:
           *
           * 1. Si id_user_type = 1 y profile_type vacío → true
           * 2. Si id_user_type = 2 y userAnswers = 0 → true
           * 3. Si id_user_type = 1 y profile_type lleno pero userAnswers = 0 → true
           */

          if (
            (idUserType === 1 && !profileType) ||
            (idUserType === 1 && profileType && userAnswersLength === 0)
          ) {
            missingResponseInvestor = true;
          }/* else if (
                  (idUserType === 2 && userAnswersLength === 0)
                ) {
                  missingResponseSeller = true;
                }*/
          console.log("missingResponseInvestor:", missingResponseInvestor)
          console.log("missingResponseSeller:", missingResponseSeller)

          this.toastrService.success('a Inviertis', 'Bienvenido');

          if (missingResponseInvestor) {
            this.step = 2;
          }/* else if(missingResponseSeller) {
                  this.step = 4;
                }*/

          this.datalayerService.push('form_submitted', {
            form_id: 'form_login',
            form_name: 'Formulario Login',
            form_location: 'modal',
            page_language: this.translateService.getCurrentLang(),
            page_location: isPlatformBrowser(this.platformId) ? window.location.href : '',
            page_path: isPlatformBrowser(this.platformId) ? window.location.pathname : '',
            method: 'email',
          });

          if (!missingResponseInvestor) {
            if (this.data?.returnUrl) {
              this.checkRoute();
            }
            this.dialogRef.close();
          }
        },
        error: (error: any) => {
          if (error.error?.type === 'not_found') {
            this.emailNotFound = true;
          } else if (error.error?.type === 'incorrect') {
            this.wrongPassword = true;
          }
          this.toastrService.error(error.error.message, 'Ingreso fallido');
        }
      });
  }
  checkRoute() {
    if (this.router.url.includes('invertir/propiedad')) {
      this.actionsService.reloadBtn(true);
      this.actionsService.favoritesDone(true);
    } else {
      this.redireccionExitosa();
    }
  }
  redireccionExitosa() {
    if (this.data?.returnUrl) {
      this.router.navigateByUrl(this.data.returnUrl);
      return;
    }
    let returnUrl = '';
    let params: any = {}
    for (const key in this.route.snapshot.queryParams) {
      if (key === 'redirect') {
        returnUrl = decodeURI(this.route.snapshot.queryParams[key]);
      } else {
        params[key] = this.route.snapshot.queryParams[key];
      }
    }
    if (returnUrl.split('?').length > 1) {
      params = JSON.parse(
        '{"' +
        decodeURI(returnUrl.split('?')[1])
          .replace(/"/g, '\\"')
          .replace(/&/g, '","')
          .replace(/=/g, '":"') +
        '"}'
      );
    }
    this.router.navigate([returnUrl.split('?')[0]], { queryParams: params });
  }

  async loginWithFacebook() {
    this.loadingLogin = true;
    this.facebookService.loginWithFacebook().then((user: any) => {

      if (user) {
        const general_data = {
          cookieHsUtk: this.cookieService.get('hubspotutk'),
          page: isPlatformBrowser(this.platformId) ? window.location.href : '',
          name: 'Login for email',
        }
        const payload = {
          ...user,
          general_data
        };

        this.authenticationService
          .loginFb(payload)
          .pipe(
            finalize(() => {
              this.loginForm.markAsPristine();
              this.loadingLogin = false;
            }),
            untilDestroyed(this)
          )
          .subscribe({
            next: () => {
              this.toastrService.success('a Inviertis', 'Bienvenido');
              this.step = 2;

              this.datalayerService.push('form_submitted', {
                form_id: 'form_login',
                form_name: 'Formulario Login',
                page_language: this.translateService.getCurrentLang(),
                page_location: isPlatformBrowser(this.platformId) ? window.location.href : '',
                page_path: isPlatformBrowser(this.platformId) ? window.location.pathname : '',
                method: 'facebook',
              });

              if (this.data?.returnUrl) {
                this.checkRoute();
              }
              this.dialogRef.close();
            },
            error: (error: any) => {
              this.toastrService.error(error.error.message, 'Ingreso fallido');
            }
          });
      }

    });
  }

  loginWithGoogle(token: any) {
    if (token) {
      const general_data = {
        cookieHsUtk: this.cookieService.get('hubspotutk'),
        page: isPlatformBrowser(this.platformId) ? window.location.href : '',
        name: 'Login for email',
      }
      const payload = {
        token,
        general_data
      };

      this.authenticationService
        .loginGoogle(payload)
        .pipe(
          finalize(() => {
            this.loginForm.markAsPristine();
            this.loadingLogin = false;
          }),
          untilDestroyed(this)
        )
        .subscribe({
          next: () => {
            this.toastrService.success('a Inviertis', 'Bienvenido');
            this.step = 2;
            this.datalayerService.push('form_submitted', {
              form_id: 'form_login',
              form_name: 'Formulario Login',
              form_location: 'modal',
              page_language: this.translateService.getCurrentLang(),
              page_location: isPlatformBrowser(this.platformId) ? window.location.href : '',
              page_path: isPlatformBrowser(this.platformId) ? window.location.pathname : '',
              method: 'google',
            });

            if (this.data?.returnUrl) {
              this.checkRoute();
            }
            this.dialogRef.close();
          },
          error: (error: any) => {
            this.toastrService.error(error.error.message, 'Ingreso fallido');
          }
        });
    }
  }

  openSignInModal() {
    this.dialog.open(SignInModalComponent, {
      maxWidth: '95vw',
      panelClass: 'icon-logo-modal',
    });
    this.dialogRef.close();
  }

  async loginAppFacebook(): Promise<void> {
    this.loadingLogin = true;

    let user: any;
    try {
      user = await this.facebookService.loginWithFacebook();
    } catch (error) {
      this.loadingLogin = false;
      this.toastrService.error('No se pudo iniciar sesión con Facebook.', 'Ingreso fallido');
      return;
    }

    if (!user || !user.authToken) {
      this.loadingLogin = false;
      this.toastrService.error('No se pudo obtener la información de Facebook', 'Ingreso fallido');
      return;
    }

    const general_data = {
      cookieHsUtk: this.cookieService.get('hubspotutk'),
      page: isPlatformBrowser(this.platformId) ? window.location.href : '',
      name: 'Login for facebook',
    }
    const payload = {
      ...user,
      general_data
    };

    this.authenticationService
      .loginFb(payload)
      .pipe(
        finalize(() => {
          this.loginForm.markAsPristine();
          this.loadingLogin = false;
        }),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => {
          this.toastrService.success('a Inviertis', 'Bienvenido');
          this.step = 2;

          this.datalayerService.push('form_submitted', {
            form_id: 'form_login',
            form_name: 'Formulario Login',
            page_language: this.translateService.getCurrentLang(),
            page_location: isPlatformBrowser(this.platformId) ? window.location.href : '',
            page_path: isPlatformBrowser(this.platformId) ? window.location.pathname : '',
            method: 'facebook',
          });

          if (this.data?.returnUrl) {
            this.checkRoute();
          }
          this.dialogRef.close();
        },
        error: (error: any) => {
          this.toastrService.error(error?.error?.message, 'Ingreso fallido');
        }
      });
  }
}